// The DE setup checklist (coverage rows 35–37): what is left before a DE in setup goes live. The
// steps come from the profile alone (no extra call): a seat with a staffed supervisor seat, the
// DE's own LiteLLM key (`litellm_key_alias` is set once a key is issued), and its delivery, which
// is what moves the DE from setup to active (`POST …/litellm-key/ack`).
//
// Provisioning, collecting and acknowledging are agency-operator routes. Other managers see the
// same checklist with the operator's part explained, never a button that would be refused.
// The issued key (`GET …/litellm-key`) is a secret like an ingest key's plaintext: it lives only
// in the collect dialog's state, behind an explicit "I've delivered it", and is dropped on close.
import { newIdempotencyKey } from "@companyos/sdk";
import {
  Button,
  Checkbox,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldDescription,
  FieldLabel,
  Input,
  Label,
  Notice,
  SettingsSection,
  type SetupStep,
  SetupSteps,
  toast,
  workerName,
} from "@companyos/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CopyIcon, KeyRoundIcon, ListChecksIcon, SendIcon } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import type { Api } from "../../api/client";
import type { DeProfile } from "../../api/types";
import type { CompanyAccess } from "../company";
import { formatDateTime } from "../format";
import { keys } from "../queries";
import { problemText } from "./de-status";

/** The checklist's steps, derived from the profile. */
export function setupSteps(de: DeProfile): SetupStep[] {
  const seated = Boolean(de.seat && de.supervisor);
  const keyed = Boolean(de.litellm_key_alias) || de.status !== "onboarding";
  const live = de.status !== "onboarding";
  const state = (done: boolean, ready: boolean) =>
    done ? ("done" as const) : ready ? ("current" as const) : ("todo" as const);
  return [
    {
      label: "Seat and supervisor",
      hint: seated
        ? `${de.seat?.title} · supervised by ${de.supervisor?.name ?? de.supervisor?.title}`
        : "The DE needs an active seat whose supervisor seat is staffed.",
      state: state(seated, true),
    },
    {
      label: "LiteLLM key",
      hint: keyed
        ? (de.litellm_key_alias ?? "Issued")
        : "A budgeted model key, provisioned by your CompanyOS operator.",
      state: state(keyed, seated),
    },
    {
      label: "Deliver the key and go live",
      hint: live
        ? "Delivered: the DE is live."
        : "The operator hands the key to the DE's runtime and confirms it.",
      state: state(live, keyed),
    },
  ];
}

export function SetupChecklist({
  api,
  company,
  de,
}: {
  api: Api;
  company: CompanyAccess;
  de: DeProfile;
}) {
  const operator = company.roles.includes("agency_operator");
  const steps = setupSteps(de);
  const seated = steps[0]?.state === "done";
  return (
    <SettingsSection
      icon={ListChecksIcon}
      title={`Before ${workerName(de.display_name)} goes live`}
      description="A DE stays in setup until its LiteLLM key is delivered."
      data-slot="de-setup"
    >
      <SetupSteps aria-label="Setup steps" steps={steps} />
      {de.provisioning_error ? (
        <Notice tone="danger" title="The last key attempt failed">
          {de.provisioning_error}
        </Notice>
      ) : null}
      {!seated ? (
        <Notice tone="warning">
          Assign {workerName(de.display_name)} to a seat with a staffed supervisor seat (Setup ›
          Seats) before a key can be issued.
        </Notice>
      ) : operator ? (
        <div className="flex flex-wrap items-center gap-2">
          {/* Both stay mounted: the provision dialog closes as the checklist moves on. */}
          <ProvisionKeyDialog
            api={api}
            companyId={company.id}
            de={de}
            trigger={!de.litellm_key_alias}
          />
          <DeliverKeyDialog
            api={api}
            companyId={company.id}
            de={de}
            trigger={Boolean(de.litellm_key_alias)}
          />
        </div>
      ) : (
        <Notice tone="info">
          {de.litellm_key_alias
            ? `The key is issued. Your CompanyOS operator delivers it to ${workerName(de.display_name)}'s runtime and confirms delivery; ${workerName(de.display_name)} then goes live.`
            : `Your CompanyOS operator provisions ${workerName(de.display_name)}'s LiteLLM key with a budget, then delivers it. ${workerName(de.display_name)} goes live when they confirm delivery.`}
        </Notice>
      )}
    </SettingsSection>
  );
}

function ProvisionKeyDialog({
  api,
  companyId,
  de,
  trigger,
}: {
  api: Api;
  companyId: string;
  de: DeProfile;
  /** Show the opening button (the step is current). */
  trigger: boolean;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [budget, setBudget] = useState("");
  const [models, setModels] = useState("");
  const [outcome, setOutcome] = useState<string | null>(null);
  const budgetId = useId();
  const modelsId = useId();
  const dollars = Number(budget);
  const valid = Number.isFinite(dollars) && dollars > 0;
  const provision = useMutation({
    mutationFn: () => {
      const list = models
        .split(",")
        .map((m) => m.trim())
        .filter(Boolean);
      return api.client.digitalEmployees.createLitellmKey({
        params: { co: companyId, id: de.membership_id },
        idempotencyKey: newIdempotencyKey(),
        body: {
          max_budget_minor: Math.round(dollars * 100),
          currency: "USD",
          ...(list.length ? { models: list } : {}),
        },
      });
    },
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: keys.de(companyId, de.membership_id) });
      const p = result.provisioning;
      if (p.state === "issued") {
        toast.add({ title: "Key issued", description: `${p.key_alias}: collect and deliver it.` });
        setOpen(false);
      } else if (p.state === "failed" || p.state === "expired")
        setOutcome(p.error ?? "The gateway refused the key.");
      else setOutcome("The attempt is still running. Check back in a minute.");
    },
  });
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (provision.isPending) return;
        setOpen(next);
        if (!next) {
          provision.reset();
          setOutcome(null);
        }
      }}
    >
      {trigger ? (
        <Button onClick={() => setOpen(true)}>
          <KeyRoundIcon aria-hidden="true" />
          Provision LiteLLM key
        </Button>
      ) : null}
      <DialogContent className="sm:max-w-lg">
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (valid) provision.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>Provision {workerName(de.display_name)}'s LiteLLM key</DialogTitle>
            <DialogDescription>
              The gateway issues a key with this budget for {workerName(de.display_name)} only. It
              is then held for you to collect and deliver.
            </DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor={budgetId}>Budget (USD)</FieldLabel>
            <Input
              id={budgetId}
              type="number"
              inputMode="decimal"
              min={0.01}
              step="any"
              required
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
            />
            <FieldDescription>LiteLLM budgets are in US dollars.</FieldDescription>
          </Field>
          <Field>
            <FieldLabel htmlFor={modelsId}>Models</FieldLabel>
            <Input
              id={modelsId}
              placeholder="gpt-4.1-mini, claude-sonnet"
              value={models}
              onChange={(e) => setModels(e.target.value)}
            />
            <FieldDescription>
              Comma-separated. Leave empty to use the gateway's default model list.
            </FieldDescription>
          </Field>
          {provision.isError || outcome ? (
            <p role="alert" className="text-sm text-danger-text">
              {outcome ?? problemText(provision.error, "The key could not be provisioned.")}
            </p>
          ) : null}
          <DialogFooter>
            <DialogClose
              disabled={provision.isPending}
              render={<Button variant="secondary" type="button" />}
            >
              Cancel
            </DialogClose>
            <Button type="submit" disabled={!valid || provision.isPending}>
              {provision.isPending ? "Provisioning…" : "Provision key"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

type Issued = { attempt: number; key_alias: string; virtual_key: string; expires_at: string };

function DeliverKeyDialog({
  api,
  companyId,
  de,
  trigger,
}: {
  api: Api;
  companyId: string;
  de: DeProfile;
  trigger: boolean;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [issued, setIssued] = useState<Issued | null>(null);
  const [delivered, setDelivered] = useState(false);
  const [copyState, setCopyState] = useState<"idle" | "done" | "failed">("idle");
  const keyId = useId();
  const confirmId = useId();
  // As in the ingest-key dialog: a response landing after its dialog session ended is dropped.
  const session = useRef(0);
  useEffect(
    () => () => {
      session.current += 1;
    },
    [],
  );

  const collect = useMutation({
    // The key goes straight into dialog state; the mutation cache keeps only the attempt number.
    mutationFn: async () => {
      const mine = session.current;
      const result = await api.client.digitalEmployees.getLitellmKey({
        params: { co: companyId, id: de.membership_id },
      });
      if (mine === session.current) setIssued(result);
      return result.attempt;
    },
  });
  const ack = useMutation({
    mutationFn: (attempt: number) =>
      api.client.digitalEmployees.ackLitellmKey({
        params: { co: companyId, id: de.membership_id },
        body: { attempt },
      }),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: keys.de(companyId, de.membership_id) });
      void qc.invalidateQueries({ queryKey: keys.digitalEmployees(companyId) });
      close();
      toast.add({
        title: "Key delivered",
        description:
          result.digital_employee.status === "active"
            ? `${workerName(de.display_name)} is live.`
            : `${workerName(de.display_name)}'s key is delivered.`,
      });
    },
  });

  const close = () => {
    session.current += 1;
    setIssued(null);
    setDelivered(false);
    setCopyState("idle");
    collect.reset();
    ack.reset();
    setOpen(false);
  };
  const copy = async () => {
    if (!issued) return;
    try {
      await navigator.clipboard.writeText(issued.virtual_key);
      setCopyState("done");
    } catch {
      setCopyState("failed");
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) {
          setOpen(true);
          collect.mutate();
        } else if (!collect.isPending && !ack.isPending) close();
      }}
    >
      {trigger ? (
        <Button
          onClick={() => {
            setOpen(true);
            collect.mutate();
          }}
        >
          <SendIcon aria-hidden="true" />
          Collect and deliver key
        </Button>
      ) : null}
      <DialogContent className="sm:max-w-lg" showCloseButton={!ack.isPending}>
        <DialogHeader>
          <DialogTitle>Deliver {workerName(de.display_name)}'s LiteLLM key</DialogTitle>
          <DialogDescription>
            Put the key in {workerName(de.display_name)}'s runtime secrets, then confirm. CompanyOS
            wipes its sealed copy and {workerName(de.display_name)} goes live.
          </DialogDescription>
        </DialogHeader>
        {collect.isPending ? (
          <p role="status" aria-label="Loading the key" className="text-sm text-muted-foreground">
            Collecting the key…
          </p>
        ) : collect.isError ? (
          <Notice tone="warning" title="No key to collect">
            {problemText(collect.error, "The key could not be collected.") === "Not found"
              ? "Only the operator who provisioned the key can collect it, and only until it expires. Provision a new key if it has expired."
              : problemText(collect.error, "The key could not be collected.")}
          </Notice>
        ) : issued ? (
          <>
            <Field>
              <FieldLabel htmlFor={keyId}>LiteLLM key ({issued.key_alias})</FieldLabel>
              <div className="flex min-w-0 gap-2">
                <Input
                  id={keyId}
                  readOnly
                  value={issued.virtual_key}
                  className="min-w-0 font-mono text-xs"
                  onFocus={(e) => e.currentTarget.select()}
                />
                <Button variant="secondary" onClick={() => void copy()}>
                  <CopyIcon aria-hidden="true" />
                  Copy
                </Button>
              </div>
              <FieldDescription aria-live="polite">
                {copyState === "done"
                  ? "Copied to the clipboard."
                  : copyState === "failed"
                    ? "Copy failed: select the key and copy it by hand."
                    : `Collect before ${formatDateTime(issued.expires_at)}.`}
              </FieldDescription>
            </Field>
            <div className="flex items-center gap-2">
              <Checkbox
                id={confirmId}
                checked={delivered}
                onCheckedChange={(v) => setDelivered(v === true)}
              />
              <Label htmlFor={confirmId}>
                I've put it in {workerName(de.display_name)}'s runtime
              </Label>
            </div>
            {ack.isError ? (
              <p role="alert" className="text-sm text-danger-text">
                {problemText(ack.error, "Delivery could not be confirmed.")}
              </p>
            ) : null}
          </>
        ) : null}
        <DialogFooter>
          <DialogClose
            disabled={ack.isPending}
            render={<Button variant="secondary" type="button" />}
          >
            Cancel
          </DialogClose>
          <Button
            disabled={!issued || !delivered || ack.isPending}
            onClick={() => issued && ack.mutate(issued.attempt)}
          >
            {ack.isPending ? "Confirming…" : "Confirm delivery"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
